import React, { useState } from "react";
import { Link, useLocation, useNavigate, Outlet } from "react-router-dom";
import {
  LayoutDashboard, IdCard, Briefcase, Brain, ScanLine, Wand2, ClipboardList,
  KanbanSquare, Mic, Video, BarChart3, LogOut, Users, FilePlus2, GitBranch, Menu, X, Zap, User
} from "lucide-react";
import { Logo } from "@/components/Logo";
import ThemeToggle from "@/components/ThemeToggle";
import { useAuth } from "@/context/AuthContext";
import { motion, AnimatePresence } from "framer-motion";

const SEEKER_NAV = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/app/passport", label: "Career Profile", icon: IdCard },
  { to: "/app/jobs", label: "Find Jobs", icon: Briefcase },
  // { to: "/app/intelligence", label: "Career Intelligence", icon: Brain },
  { to: "/app/ats", label: "ATS Checker", icon: ScanLine },
  { to: "/app/customize", label: "CV Customization", icon: Wand2 },
  // { to: "/app/autofill", label: "Application Autofill", icon: ClipboardList },
  { to: "/app/tracker", label: "Application Tracker", icon: KanbanSquare },
  { to: "/app/interview", label: "Interview Copilot", icon: Mic },
  { to: "/app/practice", label: "Face-to-Face", icon: Video },
  // { to: "/app/analytics", label: "Career Analytics", icon: BarChart3 },
];

const RECRUITER_NAV = [
  { to: "/recruiter", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/recruiter/candidates", label: "Candidate Search", icon: Users },
  { to: "/recruiter/post", label: "Post Job", icon: FilePlus2 },
  { to: "/recruiter/pipeline", label: "Pipeline", icon: GitBranch },
];

const MOBILE_SEEKER = SEEKER_NAV.slice(0, 5);

function isActive(pathname, item) {
  return item.end ? pathname === item.to : pathname.startsWith(item.to);
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [drawer, setDrawer] = useState(false);
  const recruiter = user?.role === "recruiter";
  const nav = recruiter ? RECRUITER_NAV : SEEKER_NAV;
  const mobileNav = recruiter ? RECRUITER_NAV : MOBILE_SEEKER;

  const SidebarContent = (
    <div className="flex h-full flex-col">
      <div className="px-5 py-5"><Logo /></div>
      <nav className="flex-1 space-y-1 overflow-y-auto px-3 pb-4">
        {nav.map((item) => {
          const active = isActive(location.pathname, item);
          const Icon = item.icon;
          return (
            <Link
              key={item.to}
              to={item.to}
              onClick={() => setDrawer(false)}
              data-testid={`side-nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors ${
                active ? "bg-primary text-primary-foreground shadow-glow" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
              }`}
            >
              <Icon className="h-[18px] w-[18px] shrink-0" />
              <span className="truncate">{item.label}</span>
            </Link>
          );
        })}
      </nav>
      <div className="border-t border-border p-3">
        <button
          onClick={() => { logout(); navigate("/"); }}
          data-testid="logout-btn"
          className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold text-muted-foreground hover:bg-secondary hover:text-foreground"
        >
          <LogOut className="h-[18px] w-[18px]" /> Log out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* decorative glows */}
      <div aria-hidden className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-40 right-0 h-[420px] w-[420px] rounded-full bg-electric/5 blur-3xl" />
        <div className="absolute bottom-0 left-1/4 h-[360px] w-[360px] rounded-full bg-violet/5 blur-3xl" />
      </div>

      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 border-r border-border bg-card/60 backdrop-blur-xl lg:block">
        {SidebarContent}
      </aside>

      {/* Mobile drawer */}
      <AnimatePresence>
        {drawer && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-black/40 lg:hidden" onClick={() => setDrawer(false)} />
            <motion.aside
              initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }}
              transition={{ type: "spring", stiffness: 400, damping: 40 }}
              className="fixed inset-y-0 left-0 z-50 w-72 border-r border-border bg-card lg:hidden"
            >
              <button className="absolute right-3 top-4 rounded-full p-2 hover:bg-secondary" onClick={() => setDrawer(false)}>
                <X className="h-5 w-5" />
              </button>
              {SidebarContent}
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <div className="lg:pl-64">
        {/* Top header */}
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-border bg-card/70 px-5 backdrop-blur-xl">
          <div className="flex items-center gap-3">
            <button className="rounded-full border border-border p-2 lg:hidden" onClick={() => setDrawer(true)} data-testid="app-menu-btn">
              <Menu className="h-5 w-5" />
            </button>
            <div className="lg:hidden"><Logo /></div>
            <span className="hidden text-sm font-medium text-muted-foreground lg:inline">
              {recruiter ? "Recruiter workspace" : "Your Career Profile"}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <Link
              to={recruiter ? "/recruiter/profile" : "/app/profile"}
              className="flex items-center gap-2 rounded-full border border-border bg-card px-2.5 py-1.5 pr-3.5 hover:border-electric/60 hover:bg-secondary/60 transition-all shadow-xs"
              title="View User Profile & Wallet"
              data-testid="header-user-profile-pill"
            >
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-electric to-violet text-xs font-bold text-white shadow-xs">
                {user?.name?.[0]?.toUpperCase() ?? "U"}
              </span>
              <div className="flex flex-col text-left">
                <span className="text-xs font-semibold text-foreground truncate max-w-[140px] sm:max-w-[180px]">{user?.name}</span>
                <span className="text-[10px] text-muted-foreground font-medium flex items-center gap-1">
                  <Zap className="h-2.5 w-2.5 fill-electric text-electric" /> {user?.credits ?? 0} Credits · {user?.plan || "Free"}
                </span>
              </div>
            </Link>
          </div>
        </header>

        <main className="px-5 pb-28 pt-6 sm:px-8 lg:pb-10">
          <div className="mx-auto w-full max-w-6xl">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 border-t border-border bg-card/80 backdrop-blur-xl lg:hidden">
        <div className="flex items-center justify-around px-2 py-2">
          {mobileNav.map((item) => {
            const active = isActive(location.pathname, item);
            const Icon = item.icon;
            return (
              <Link key={item.to} to={item.to} className="flex flex-1 flex-col items-center gap-0.5 py-1"
                data-testid={`bottom-nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}>
                <Icon className={`h-5 w-5 ${active ? "text-electric" : "text-muted-foreground"}`} />
                <span className={`text-[10px] font-semibold ${active ? "text-electric" : "text-muted-foreground"}`}>
                  {item.label.split(" ")[0]}
                </span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
